import React from 'react';
import { ArrowRight, ArrowUp, Clock, Sun, Moon, Star, Sparkles, Heart, History, Calculator, BookOpen } from 'lucide-react';
import AuthorBio from '../components/AuthorBio';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';

interface ToolGuideProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    steps: { title: string; description: string }[];
    linkTo: string;
    linkText: string;
    accentColor: string;
}

const ToolGuide: React.FC<ToolGuideProps> = ({ icon, title, description, steps, linkTo, linkText, accentColor }) => (
    <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mb-6">
        <div className="flex items-center gap-3 mb-4">
            <div className={`bg-${accentColor}/20 p-3 rounded-xl`}>
                {icon}
            </div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        <p className="text-gray-300 text-sm leading-relaxed mb-4">{description}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-300 mb-4">
            {steps.map((step, i) => (
                <div key={i} className="bg-black/20 p-4 rounded-xl">
                    <div className={`text-${accentColor} font-bold mb-2`}>Step {i + 1}: {step.title}</div>
                    <p>{step.description}</p>
                </div>
            ))}
        </div>
        <a 
            href={linkTo} 
            className={`inline-flex items-center gap-2 text-${accentColor} text-sm hover:underline font-medium`}
        >
            {linkText} <ArrowRight size={14} />
        </a>
    </article>
);

const GettingStartedPage: React.FC = () => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'Getting Started Guide - How to Use ZodiAge Tools | ZodiAge',
    description: 'Learn how to use ZodiAge tools including zodiac calculator, tarot readings, personality profiles, and more. Step-by-step guides for each astrological feature.',
    ogImage: 'https://zodiage.com/logo.png'
  });
    return (
        <div className="space-y-12 animate-in fade-in duration-500 pb-12">
            {/* Header */}
            <article className="max-w-4xl mx-auto">
                <header className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                        Getting Started Guide – How to Use ZodiAge Tools
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                        Learn how to use each tool on ZodiAge effectively. This comprehensive guide explains 
                        the step-by-step process for every feature, from age calculation to tarot readings.
                    </p>
                </header>

                {/* Quick Overview */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mb-8">
                    <h2 className="text-xl font-bold text-white mb-3">Quick Overview</h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        ZodiAge offers a suite of interconnected tools for age calculation, astrological exploration, 
                        and personal reflection. All tools are free to use, with some features having daily usage limits 
                        to encourage thoughtful engagement. Your data is stored locally in your browser and never uploaded 
                        to external servers.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div className="bg-black/20 p-3 rounded-xl text-center">
                            <Calculator size={20} className="mx-auto mb-2 text-primary" />
                            <div className="text-white font-medium">Age Calculator</div>
                        </div>
                        <div className="bg-black/20 p-3 rounded-xl text-center">
                            <Sun size={20} className="mx-auto mb-2 text-yellow-400" />
                            <div className="text-white font-medium">Western Zodiac</div>
                        </div>
                        <div className="bg-black/20 p-3 rounded-xl text-center">
                            <Moon size={20} className="mx-auto mb-2 text-red-400" />
                            <div className="text-white font-medium">Chinese Zodiac</div>
                        </div>
                        <div className="bg-black/20 p-3 rounded-xl text-center">
                            <Sparkles size={20} className="mx-auto mb-2 text-purple-400" />
                            <div className="text-white font-medium">Tarot</div>
                        </div>
                        <div className="bg-black/20 p-3 rounded-xl text-center">
                            <Star size={20} className="mx-auto mb-2 text-pink-400" />
                            <div className="text-white font-medium">Personality Profile</div>
                        </div>
                        <div className="bg-black/20 p-3 rounded-xl text-center">
                            <Heart size={20} className="mx-auto mb-2 text-green-400" />
                            <div className="text-white font-medium">Life Progress</div>
                        </div>
                    </div>
                </section>
            </article>

            {/* Detailed Tool Guides */}
            <div className="max-w-4xl mx-auto px-4">
                <h2 className="text-2xl font-display font-bold text-white mb-6">Step-by-Step Tool Instructions</h2>

                {/* Age Calculator Guide */}
                <ToolGuide
                    icon={<Calculator size={24} className="text-primary" />}
                    title="Age Calculator"
                    description="Calculate your exact age in years, months, days, hours, minutes, and seconds. The most precise age calculator with real-time updates."
                    steps={[
                        { title: "Enter Birth Date", description: "Use the date picker to select your date of birth. You can type directly or use the calendar dropdown." },
                        { title: "Add Birth Time (Optional)", description: "For maximum precision, enter your birth time. This enables calculation down to the exact second." },
                        { title: "View Results", description: "See your age displayed in multiple formats: total years, months, days, and even a live-updating seconds counter." }
                    ]}
                    linkTo="/"
                    linkText="Go to Age Calculator"
                    accentColor="primary"
                />

                {/* Western Zodiac Guide */}
                <ToolGuide
                    icon={<Sun size={24} className="text-yellow-400" />}
                    title="Western Zodiac (Sun, Moon & Rising)"
                    description="Discover your 'Big Three' — Sun, Moon, and Rising signs — and check compatibility with any other zodiac sign."
                    steps={[
                        { title: "Enter Birth Info", description: "Input your birth date and time (on the Home page). Birth time is needed for an accurate Rising sign." },
                        { title: "View Your Signs", description: "Navigate to the Zodiac page to see your Sun sign (identity), Moon sign (emotions), and Rising sign (outer self)." },
                        { title: "Check Compatibility", description: "Select a partner sign from the dropdown and click 'Reveal Connection' to see love, work, and friendship insights." }
                    ]}
                    linkTo="/zodiac"
                    linkText="Go to Zodiac Page"
                    accentColor="yellow-400"
                />

                {/* Chinese Zodiac Guide */}
                <ToolGuide
                    icon={<Moon size={24} className="text-red-400" />}
                    title="Chinese Zodiac"
                    description="Find your Chinese zodiac animal based on your birth year, including your element and yin/yang polarity."
                    steps={[
                        { title: "Enter Birth Year", description: "Type the year you were born (e.g., 1990). The calculator accepts years from 1900 onward." },
                        { title: "Get Your Animal", description: "Click 'Reveal My Chinese Zodiac' to see your animal sign, element, and polarity." },
                        { title: "Explore Your Profile", description: "Read about your animal's traits, compatibility, career tendencies, and lucky attributes." }
                    ]}
                    linkTo="/chinese-zodiage"
                    linkText="Go to Chinese Zodiac"
                    accentColor="red-400"
                />

                {/* Tarot Guide */}
                <ToolGuide
                    icon={<Sparkles size={24} className="text-purple-400" />}
                    title="Daily Tarot Card"
                    description="Draw a card from the Major Arcana for daily reflection. Each card offers a theme to contemplate throughout your day."
                    steps={[
                        { title: "Draw a Card", description: "Click 'Draw Daily Card' to receive a random card from the 22 Major Arcana. Cards can appear upright or reversed." },
                        { title: "Read the Interpretation", description: "Review the card's symbolism, keywords, and meaning. Reversed cards offer an alternative perspective." },
                        { title: "Reflect & Return", description: "Journal about how the card relates to your day. Return tomorrow for a new card (daily refresh)." }
                    ]}
                    linkTo="/tarot"
                    linkText="Go to Tarot Page"
                    accentColor="purple-400"
                />

                {/* Personality Profile Guide */}
                <ToolGuide
                    icon={<Star size={24} className="text-pink-400" />}
                    title="Personality Profile Generator"
                    description="Generate an AI-powered personality profile that combines your Western and Chinese zodiac signs."
                    steps={[
                        { title: "Confirm Birth Date", description: "Your birth date from the Home page is used automatically. You can adjust if needed." },
                        { title: "Choose a Tone", description: "Select a voice: Mystical, Playful, Professional, or Poetic. This affects how your profile is written." },
                        { title: "Generate Profile", description: "Click 'Generate' to create a unique profile with headline, traits, lucky attributes, and deep analysis." }
                    ]}
                    linkTo="/personality"
                    linkText="Go to Personality Page"
                    accentColor="pink-400"
                />

                {/* Life Progress Guide */}
                <ToolGuide
                    icon={<Heart size={24} className="text-green-400" />}
                    title="Life Progress Calculator"
                    description="Visualize what percentage of your expected lifespan you've lived as a reflective tool for intentional living."
                    steps={[
                        { title: "Enter Birth Date", description: "Go to the Home page first and enter your birth date. Life Progress uses this to calculate your current age." },
                        { title: "View Progress Bar", description: "Navigate to Life Progress to see your life as a percentage bar. 'Years Gone' and 'Years Remaining' are shown." },
                        { title: "Adjust Expectancy", description: "Use the slider to change your assumed life expectancy. See how different assumptions change the percentage." }
                    ]}
                    linkTo="/progress"
                    linkText="Go to Life Progress"
                    accentColor="green-400"
                />

                {/* Life Milestones Guide */}
                <ToolGuide
                    icon={<History size={24} className="text-cyan-400" />}
                    title="Life Milestones Timeline"
                    description="Explore major astrological life cycles mapped to your personal timeline, from Saturn Return to Chiron Return."
                    steps={[
                        { title: "Enter Birth Date", description: "Your birth date determines which milestones you've passed and which are ahead." },
                        { title: "Browse Milestones", description: "Scroll through milestones like Saturn Square (age 7), Saturn Return (age 30), and Chiron Return (age 50)." },
                        { title: "Explore Phases", description: "Click any milestone to expand it and optionally generate a personalized cosmic insight." }
                    ]}
                    linkTo="/timeline"
                    linkText="Go to Timeline"
                    accentColor="cyan-400"
                />
            </div>

            {/* General Usage Tips */}
            <div className="max-w-4xl mx-auto px-4">
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-4">General Usage Tips</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
                        <div>
                            <h4 className="font-bold text-white mb-2">Data Storage</h4>
                            <p>All your data is stored locally in your browser using localStorage. Nothing is uploaded to servers. Clear your browser data to reset everything.</p>
                        </div>
                        <div>
                            <h4 className="font-bold text-white mb-2">Daily Cooldowns</h4>
                            <p>Some features (compatibility checks, tarot draws, personality profiles) have 24-hour cooldowns for free users to encourage thoughtful use.</p>
                        </div>
                        <div>
                            <h4 className="font-bold text-white mb-2">Birth Time Importance</h4>
                            <p>For the most accurate Rising sign, enter your exact birth time. Without it, the Rising sign is estimated. The Moon sign uses your birth date only.</p>
                        </div>
                        <div>
                            <h4 className="font-bold text-white mb-2">Mobile Friendly</h4>
                            <p>All tools work on mobile devices. The interface adapts to smaller screens. For best experience, use landscape mode on tablets.</p>
                        </div>
                    </div>
                </section>
            </div>

            {/* Quick Links */}
            <div className="max-w-4xl mx-auto px-4">
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-4">Ready to Start?</h2>
                    <p className="text-gray-300 text-sm mb-4">Choose a tool below to begin your journey:</p>
                    <div className="flex flex-wrap gap-3">
                        <a href="/" className="bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-full text-sm font-medium transition-colors">Age Calculator</a>
                        <a href="/zodiac" className="bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">Western Zodiac</a>
                        <a href="/chinese-zodiage" className="bg-red-400/20 hover:bg-red-400/30 text-red-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">Chinese Zodiac</a>
                        <a href="/tarot" className="bg-purple-400/20 hover:bg-purple-400/30 text-purple-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">Tarot</a>
                        <a href="/personality" className="bg-pink-400/20 hover:bg-pink-400/30 text-pink-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">Personality</a>
                        <a href="/progress" className="bg-green-400/20 hover:bg-green-400/30 text-green-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">Life Progress</a>
                        <a href="/timeline" className="bg-cyan-400/20 hover:bg-cyan-400/30 text-cyan-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">Timeline</a>
                    </div>
                </section>
            </div>

            {/* Author Bio - E-E-A-T Signal */}
            <div className="max-w-4xl mx-auto px-4 mt-8">
                <AuthorBio variant="compact" topic="tool usage guides and tutorials" />
            </div>

            <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
            
            {/* Back to Top Button */}
            <div className="flex justify-center mt-8">
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105 hover:shadow-purple-600/50"
                >
                    <ArrowUp size={18} />
                    <span>Back to Top</span>
                </button>
            </div>
        </div>
    );
};

export default GettingStartedPage;
